import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Asset, ImportUrlRequest, PlaybackRequest, PlaybackResponse, UploadCredentialsRequest, UploadCredentialsResponse, UploadPolicy } from './models';
import { responseErrorMessage } from './errors';

@Injectable({ providedIn: 'root' })
export class VdocipherApiService {
  readonly apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  listAssets(): Promise<Asset[]> {
    return this.get<Asset[]>('/assets');
  }

  importUrl(request: ImportUrlRequest): Promise<Asset> {
    return this.post<Asset>('/assets/import-url', {
      asset: {
        title: request.title,
        source_url: request.sourceUrl,
        folder_id: request.folderId,
      },
    });
  }

  uploadCredentials(request: UploadCredentialsRequest): Promise<UploadCredentialsResponse> {
    return this.post<UploadCredentialsResponse>('/assets/upload-credentials', {
      asset: {
        title: request.title,
        folder_id: request.folderId,
      },
    });
  }

  uploadComplete(assetId: number): Promise<Asset> {
    return this.post<Asset>(`/assets/${assetId}/upload-complete`, {});
  }

  uploadFile(upload: UploadPolicy, file: File, onProgress: (progress: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('policy', upload.policy);
      formData.append('key', upload.key);
      formData.append('x-amz-signature', upload['x-amz-signature']);
      formData.append('x-amz-algorithm', upload['x-amz-algorithm']);
      formData.append('x-amz-date', upload['x-amz-date']);
      formData.append('x-amz-credential', upload['x-amz-credential']);
      formData.append('success_action_status', '201');
      formData.append('success_action_redirect', '');
      formData.append('file', file, file.name);

      const request = new XMLHttpRequest();
      request.open('POST', upload.uploadLink);
      request.upload.onprogress = event => {
        if (!event.lengthComputable) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      };
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
          onProgress(100);
          resolve();
          return;
        }

        reject(new Error(request.responseText || `Upload failed with status ${request.status}`));
      };
      request.onerror = () => reject(new Error('Upload failed due to a network error'));
      request.onabort = () => reject(new Error('Upload was cancelled'));
      request.send(formData);
    });
  }

  getPlayback(assetId: number, request: PlaybackRequest = {}): Promise<PlaybackResponse> {
    return this.post<PlaybackResponse>(`/assets/${assetId}/playback`, request);
  }

  syncAsset(assetId: number): Promise<Asset> {
    return this.post<Asset>(`/assets/${assetId}/sync`, {});
  }

  deleteAsset(assetId: number): Promise<void> {
    return this.request<void>(`/assets/${assetId}`, { method: 'DELETE' });
  }

  private async get<T>(path: string): Promise<T> {
    return this.request<T>(path);
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}${path}`, init);
    if (!response.ok) throw new Error(await responseErrorMessage(response));
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
}
